import { ApiClient } from '@momonos/api-client'
import type { SessionUser } from '@momonos/shared'
import { Button, cn, FullscreenLoader, ToastProvider, useToast } from '@momonos/ui'
import { LogOut } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { API_URL } from './lib/config'
import {
  clearSession,
  loadBranch,
  loadToken,
  loadUser,
  type SectionId,
  saveBranch,
  saveToken,
  saveUser,
  sectionsFor,
} from './lib/session'
import { ActivityScreen } from './pages/Activity'
import { AssistantScreen } from './pages/Assistant'
import { CampaignsScreen } from './pages/Campaigns'
import { CustomersScreen } from './pages/Customers'
import { DashboardScreen } from './pages/Dashboard'
import { IntelligenceScreen } from './pages/Intelligence'
import { InventoryScreen } from './pages/Inventory'
import { MenuScreen } from './pages/Menu'
import { OrdersScreen } from './pages/Orders'
import { SignIn } from './pages/SignIn'
import './styles.css'

export function App() {
  return (
    <ToastProvider>
      <Admin />
    </ToastProvider>
  )
}

function Admin() {
  const { show } = useToast()
  const [token, setToken] = useState<string | null>(() => loadToken())
  const [user, setUser] = useState<SessionUser | null>(() => loadUser())
  const [branchId, setBranchId] = useState<string | null>(() => loadBranch())
  const [section, setSection] = useState<SectionId>('dashboard')
  const [isRestoring, setRestoring] = useState(true)

  const signOut = useCallback(() => {
    clearSession()
    setToken(null)
    setUser(null)
    setBranchId(null)
  }, [])

  const api = useMemo(
    () =>
      new ApiClient({
        baseUrl: API_URL,
        getAccessToken: () => token,
        onTokenRefreshed: (fresh) => {
          setToken(fresh)
          saveToken(fresh)
        },
        onSessionExpired: signOut,
      }),
    [token, signOut],
  )

  /**
   * A page reload has no access token in memory, but the refresh cookie is
   * still valid — so try to resume before showing a sign-in form.
   */
  useEffect(() => {
    let cancelled = false

    async function restore() {
      try {
        const me = await api.get<SessionUser>('/v1/auth/me')
        if (cancelled) return

        setUser(me)
        saveUser(me)

        // Read the stored branch rather than the state closure: the effect
        // must not depend on a value it only reads once at startup.
        const next = loadBranch() ?? me.activeBranchId ?? me.branches[0]?.id ?? null
        setBranchId(next)
        saveBranch(next)
      } catch {
        if (!cancelled) signOut()
      } finally {
        if (!cancelled) setRestoring(false)
      }
    }

    if (token) void restore()
    else setRestoring(false)

    return () => {
      cancelled = true
    }
  }, [api, token, signOut])

  if (isRestoring) return <FullscreenLoader label="Signing in" />

  if (!token || !user) {
    return (
      <SignIn
        apiUrl={API_URL}
        onSignedIn={(result) => {
          setToken(result.accessToken)
          saveToken(result.accessToken)
          setUser(result.user)
          saveUser(result.user)

          const next = result.user.activeBranchId ?? result.user.branches[0]?.id ?? null
          setBranchId(next)
          saveBranch(next)
          setRestoring(false)
        }}
      />
    )
  }

  const sections = sectionsFor(user.role)
  const active = sections.some((entry) => entry.id === section) ? section : sections[0]?.id

  return (
    <div className="flex min-h-dvh bg-surface">
      <nav className="flex w-56 shrink-0 flex-col border-surface-border border-r p-4">
        <p className="mb-6 font-bold text-momo-white text-xl tracking-tight">MOMONOS</p>

        <div className="space-y-1">
          {sections.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setSection(entry.id)}
              className={cn(
                'w-full rounded-lg px-3 py-2.5 text-left font-medium transition-colors',
                entry.id === active
                  ? 'bg-momo-red text-momo-white'
                  : 'text-content-secondary hover:bg-surface-raised',
              )}
            >
              {entry.label}
            </button>
          ))}
        </div>

        <div className="mt-auto space-y-3 border-surface-border border-t pt-4">
          {/* FR-AUTH-6 — switching branch without signing out. */}
          {user.branches.length > 1 && (
            <label className="block">
              <span className="mb-1 block text-content-muted text-xs">Branch</span>
              <select
                value={branchId ?? ''}
                onChange={async (event) => {
                  const next = event.target.value
                  try {
                    const result = await api.post<{ accessToken: string; user: SessionUser }>(
                      '/v1/auth/switch-branch',
                      { branchId: next },
                    )
                    setToken(result.accessToken)
                    saveToken(result.accessToken)
                    setUser(result.user)
                    saveUser(result.user)
                    setBranchId(next)
                    saveBranch(next)
                  } catch {
                    show('Could not switch branch', { tone: 'error' })
                  }
                }}
                className="w-full rounded-lg border border-surface-border bg-surface-raised px-2 py-2 text-content text-sm"
              >
                {user.branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div>
            <p className="text-content text-sm">{user.name}</p>
            <p className="text-content-muted text-xs capitalize">{user.role.replace('_', ' ')}</p>
          </div>

          <Button
            variant="ghost"
            fullWidth
            onClick={async () => {
              try {
                await api.post('/v1/auth/logout')
              } finally {
                signOut()
              }
            }}
          >
            <LogOut aria-hidden="true" className="size-4" />
            Sign out
          </Button>
        </div>
      </nav>

      <main className="flex-1 overflow-y-auto p-6">
        {active === 'dashboard' && <DashboardScreen api={api} branchId={branchId} />}
        {active === 'orders' && <OrdersScreen api={api} branchId={branchId} />}
        {active === 'menu' && <MenuScreen api={api} branchId={branchId} />}
        {active === 'inventory' && <InventoryScreen api={api} branchId={branchId} />}
        {active === 'customers' && <CustomersScreen api={api} branchId={branchId} />}
        {active === 'campaigns' && <CampaignsScreen api={api} branchId={branchId} />}
        {active === 'intelligence' && <IntelligenceScreen api={api} branchId={branchId} />}
        {active === 'assistant' && <AssistantScreen api={api} branchId={branchId} />}
        {active === 'activity' && <ActivityScreen api={api} branchId={branchId} />}
      </main>
    </div>
  )
}
